import {i18n} from '../i18n/i18n'

import React, {Component} from 'react'
import {ConfirmParams} from '../components/Confirm/Confirm.state'
import routes from '../routes/routes'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import itemService from '../services/item.service'
import storageService from '../services/storage.service'
import customLabelApi, {
    CustomLabelCreateRequest
} from '../apis/customLabel.api'
import itemApi, {ItemData} from '../apis/item.api'
import {ItemType} from '../apis/ItemType.api'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import InputText from '../components/InputText'
import Row from '../components/Row/Row'
import SearchPicker, {SearchPickerOption} from '../components/SearchPicker'
import InputFile from '../components/InputFile'
import Modal from '../components/Modal'
import Checkbox from '../components/Checkbox'
import {useUserPlan} from "../utils/userPlan.util";
import Tooltip from '../components/Tooltip/Tooltip'
import Icon from '../components/Icon'

interface Props {
    isCreateModalOpen: boolean
    setIsCreateModalOpen: (value: boolean) => void
    onSuccess?: () => void
}

interface State {
    name: string
    item: string
    csvFilename: string
    apiUrl: string
    printNodePrinterId: string
    keepVersionHistory: boolean
    itemId: number | null
    items: ItemData[]
    csv: File | null
    showPopupError: boolean
    errorMessage: string
}

class CustomLabelCreateForm extends Component<Props, State> {
    userPlan = useUserPlan();

    initialState: State = {
        name: '',
        item: '',
        csvFilename: '',
        apiUrl: '',
        printNodePrinterId: '',
        // On by default: history is the safer starting point, and a label
        // only starts recording once it is actually activated.
        keepVersionHistory: true,
        itemId: null,
        csv: null,
        items: [],
        showPopupError: false,
        errorMessage: '',
    }

    constructor(props: Props) {
        super(props)
        this.state = this.initialState

        this.togglePopupError = this.togglePopupError.bind(this)
        this.handleCancelPopupError = this.handleCancelPopupError.bind(this)
        this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
        this.handleCreateError = this.handleCreateError.bind(this)
    }

    closeCreateModal = () => {
        this.props.setIsCreateModalOpen(false)
        this.setState(this.initialState)
    }

    get itemSearchOptions() {
        return itemService.searchOptions(this.state.items)
    }

    togglePopupError = (errorMsg: string) => {
        this.setState((prevState) => ({showPopupError: !prevState.showPopupError, errorMessage: errorMsg}))
    }

    handleCancelPopupError() {
        this.togglePopupError('')
    }

    handleSubscribeClick() {
        this.togglePopupError('')
        // this.props.history.push(routes.userSubscriptions)
        this.closeCreateModal()
    }

    handleCreateError(errorMessage: string) {
        if (errorMessage == 'Free account user can only have up to 2 labels.') {
            this.togglePopupError(i18n('Free account user can only have up to 2 labels.'))
        } else {
            alertService.error(errorMessage)
        }
    }

    handleInputChange = (name: string, value: string) => {
        if (name === 'name') this.setState({name: value})
        else if (name === 'item') this.setState({item: value})
        else if (name === 'apiUrl') this.setState({apiUrl: value})
        else if (name === 'printNodePrinterId') this.setState({printNodePrinterId: value})
    }

    handleItemSearch = (name: string, term: string) => {
        const types: ItemType[] = ['production', 'receivable']
        itemApi.searchByCode(term, types, items => {
            if (!items) return
            this.setState({items})
        })
    }

    handleItemBlur = () => {
        this.setState({items: []})
    }

    handleItemSelect = (name: string, option: SearchPickerOption) => {
        const {value} = option
        const itemId = option.id as number
        const item = itemService.find(this.state.items, itemId)
        if (!item) return
        this.setState({items: [], item: value, itemId})
    }

    handleFileChange = (name: string, file: File | null, filename: string) => {
        if (name === 'csv') {
            this.setState({
                csv: file,
                csvFilename: filename
            })
        }
    }

    handleCreateConfirm = () => {
        const {name, csv, csvFilename, apiUrl, itemId, printNodePrinterId, keepVersionHistory} = this.state
        const payload: CustomLabelCreateRequest = {}
        if (name) payload.name = name
        if (csv) payload.csv = csv
        if (apiUrl) payload.apiUrl = apiUrl
        if (itemId) payload.item = itemId
        if (csvFilename) payload.csvFilename = csvFilename
        if (printNodePrinterId) payload.printNodePrinterId = printNodePrinterId
        // Sent as form data, so the boolean has to travel as 1/0.
        payload.keepVersionHistory = keepVersionHistory ? '1' : '0'

        customLabelApi.createWithError(payload, customLabel => {
            if (!customLabel) return
            alertService.success('Successfully created custom label')
            // this.props.history.push(routes.systemLabels)

            this.props.onSuccess?.()
            this.closeCreateModal()

        }, error => {
            const {response} = error
            const unauthorized = (response && response.status === 401)
            const requestError = (response && response.data.message)
            const message = requestError || error.message
            if (unauthorized) {
                storageService.remove('authToken')
                window.location.href = routes.signIn
            }
            this.handleCreateError(message)
        })
    }

    handleVersionHistoryToggle = () => {
        this.setState(prev => ({keepVersionHistory: !prev.keepVersionHistory}))
    }

    handleCreateClick = () => {
        const confirm: ConfirmParams = {
            id: 1,
            question: i18n('Please review custom label details above and then confirm'),
            confirmText: i18n('Confirm'),
            cancelText: i18n('Cancel'),
            onConfirm: () => this.handleCreateConfirm()
        }
        confirmService.push(confirm)
    }


    render() {
        if (!this.props.isCreateModalOpen) return null

        const {isFree, isNotFreeAndStarter, isLabelBuilderUser} = this.userPlan;
        const {showPopupError, errorMessage} = this.state

        return (
            <Modal
                title={i18n('Create Custom Label')}
                isOpen={this.props.isCreateModalOpen}
                onClose={this.closeCreateModal}
            >
                <Card flat={true}>
                    {showPopupError && (
                        <>
                            <div
                                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                                onClick={this.handleCancelPopupError}
                            ></div>
                            <div className="fixed inset-0 flex items-center justify-center z-50">
                                <div className="bg-white shadow-lg w-80 p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                        Subscription Required
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {errorMessage}
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            className="bg-blue-600 text-white px-4 py-2"
                                            onClick={this.handleSubscribeClick}
                                        >
                                            Subscribe
                                        </button>
                                        <button
                                            className="bg-gray-300 text-gray-700 px-4 py-2"
                                            onClick={this.handleCancelPopupError}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <Row column={2}>
                        <InputText
                            name="name"
                            value={this.state.name}
                            label={i18n('Name')}
                            onChange={this.handleInputChange}/>
                        {!isLabelBuilderUser && !isFree
                            ? (
                                <SearchPicker
                                    name="item"
                                    value={this.state.item}
                                    options={this.itemSearchOptions}
                                    label={i18n('Item code')}
                                    onSearch={this.handleItemSearch}
                                    onChange={this.handleInputChange}
                                    onSelect={this.handleItemSelect}
                                    onBlur={this.handleItemBlur}/>
                            )
                            : null}
                        <InputFile
                            className="mb-0"
                            name="csv"
                            filename={this.state.csvFilename}
                            accept=".csv"
                            label="CSV"
                            placeholder={i18n('Choose csv')}
                            onChange={this.handleFileChange}/>
                        {!isLabelBuilderUser && isNotFreeAndStarter
                            ? (
                                <InputText
                                    name="apiUrl"
                                    value={this.state.apiUrl}
                                    label={i18n('API URL')}
                                    onChange={this.handleInputChange}/>
                            )
                            : null}
                        <div className="block w-full">
                            <div className="flex items-center gap-1 mb-1">
                                <span>{i18n('PrintNode Printer ID (Optional)')}</span>
                                <Tooltip
                                    text={i18n('Optional default printer for PrintNode ZPL/PDF API printing. If an exposed API request sends printer_id, that request value is used instead. Browser and markup-only print methods do not use this field.')}
                                    maxWidth={320}
                                >
                                    <span className="flex items-center">
                                        <Icon
                                            name="question"
                                            size="small"
                                            color="gray"
                                        />
                                    </span>
                                </Tooltip>
                            </div>
                            <InputText
                                name="printNodePrinterId"
                                value={this.state.printNodePrinterId}
                                type="number"
                                onChange={this.handleInputChange}/>
                        </div>
                    </Row>
                    <div className="mb-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                name="keepVersionHistory"
                                label={i18n('Keep version history')}
                                checked={this.state.keepVersionHistory}
                                onChange={this.handleVersionHistoryToggle}/>
                            <Tooltip
                                text={i18n('Each time the design is saved and activated, that version is kept so it can be reviewed or restored later. Drafts are not kept.')}
                                maxWidth={320}>
                                <span className="flex items-center">
                                    <Icon name="question" size="small" color="gray"/>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                    <CardButton
                        proceedText={i18n('Create')}
                        cancelText={i18n('Cancel')}
                        onProceedClick={this.handleCreateClick}
                        onCancelClick={this.closeCreateModal}
                    />
                </Card>
            </Modal>
        )
    }
}

export default CustomLabelCreateForm